import { useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { CalendarDays, Hourglass, Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useAppStore } from "@/store/store";
import { useShallow } from "zustand/shallow";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// One row in the FAB speed-dial: icon tile + title + subtitle. ``highlight``
// promotes an action with the brand-green treatment (used for the walk-in
// entry point so it reads as the primary "guest just arrived" shortcut).
function FabAction({
  icon: Icon,
  title,
  subtitle,
  highlight = false,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  highlight?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl p-2.5 text-left transition-colors ${
        highlight
          ? "border border-green-200 bg-green-50 hover:bg-green-100/70"
          : "border border-transparent hover:bg-surface-alt"
      }`}
    >
      <span
        className={`flex size-12 shrink-0 items-center justify-center rounded-xl ${
          highlight ? "bg-green-600 text-white" : "bg-surface-alt text-text-secondary"
        }`}
      >
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block text-[15px] font-bold leading-tight ${
            highlight ? "text-green-700" : "text-text-primary"
          }`}
        >
          {title}
        </span>
        <span className="mt-0.5 block text-[13px] leading-tight text-text-secondary">
          {subtitle}
        </span>
      </span>
    </button>
  );
}

// Floating "+" that opens a speed-dial of create actions. Shared by the mobile
// bottom nav (Navbar) and the tablet-portrait affordance (DashboardLayout) so
// both surfaces stay in sync — one place to wire the walk-in modal later.
// ``className`` styles the FAB itself; callers pass their shape (rounded-2xl on
// mobile, rounded-full on tablet).
export default function FabMenu({ className }: { className?: string }) {
  const { openNewReservation, openNewWaitlist, setShowSidebar } = useAppStore(
    useShallow((s) => ({
      openNewReservation: s.openNewReservation,
      openNewWaitlist: s.openNewWaitlist,
      setShowSidebar: s.setShowSidebar,
    })),
  );
  const { pathname } = useRouterState().location;
  const navigate = useNavigate();
  // Nueva reserva / waitlist both need the Sidebar surface mounted.
  // Desktop mounts it on "/"; everywhere else (mobile bottom nav, tablet
  // FAB) we have to bounce off to /reservas so the surface has a place
  // to render.
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [open, setOpen] = useState(false);

  // Where can the Sidebar render right now? Three valid contexts:
  //   1. ``/`` on desktop — the always-visible lateral Sidebar.
  //   2. ``/reservas`` without a ``?view=`` — the page-as-Sidebar
  //      fallback rendered by ``routes/reservas.tsx``.
  //   3. ``/reservas`` *with* a ``?view=`` — ReservasPage mounts a
  //      sheet host driven by ``showSidebar``, so the FAB just needs
  //      to flip that flag instead of navigating away.
  //
  // The previous logic ignored case 3 and always bounced to
  // ``/reservas`` (stripping the active ``view=timeline`` / similar),
  // which is why tapping the mobile bottom FAB while looking at the
  // timeline kicked the host out to the reservation list.
  const search = useRouterState().location.search as { view?: string };
  const isReservasWithView = pathname === "/reservas" && Boolean(search.view);
  const sidebarIsMounted =
    (pathname === "/" && !isMobile) || (pathname === "/reservas" && !search.view);
  const ensureSidebar = () => {
    if (isReservasWithView) {
      // ReservasPage will host the Sidebar in its sheet — flip the
      // flag so the sheet opens and the openNewReservation /
      // openNewWaitlist surface has somewhere to land. No navigation.
      setShowSidebar(true);
      return;
    }
    if (!sidebarIsMounted) navigate({ to: "/reservas", search: {} });
  };

  const handleNuevaReserva = () => {
    setOpen(false);
    ensureSidebar();
    openNewReservation();
  };

  const handleAgregarLista = () => {
    setOpen(false);
    ensureSidebar();
    openNewWaitlist();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            aria-label={open ? "Cerrar menú" : "Agregar"}
            size="icon-lg"
            className={className}
          />
        }
      >
        <Plus
          className={`w-5 h-5 transition-transform duration-200 ${open ? "rotate-45" : ""}`}
          aria-hidden="true"
        />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={12}
        className="w-[20rem] max-w-[calc(100vw_-_1.5rem)] gap-1 rounded-3xl p-2"
      >
        <FabAction
          icon={CalendarDays}
          title="Nueva reserva"
          subtitle="Programar para una hora futura"
          onClick={handleNuevaReserva}
        />
        <FabAction
          icon={Hourglass}
          title="Agregar a lista de espera"
          subtitle="Cliente llegó sin reserva"
          highlight
          onClick={handleAgregarLista}
        />
      </PopoverContent>
    </Popover>
  );
}
